/**
 * Application Constants
 *
 * Central configuration for the application.
 * Modify these values to match your backend API.
 */

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

/**
 * Authentication Configuration
 *
 * IMPORTANT: Change these key names to be unique to your application
 * to avoid conflicts with other apps on the same domain.
 */
export const AUTH_CONFIG = {
  TOKEN_KEY: 'qrphone_admin_token',
  REFRESH_TOKEN_KEY: 'qrphone_admin_refresh_token',
  USER_KEY: 'qrphone_admin_user',
  ROLE_KEY: 'qrphone_admin_role',
  PLAN_MODULES_KEY: 'qrphone_admin_plan_modules',
  TOKEN_EXPIRATION_KEY: 'qrphone_admin_token_expiration',
  SESSION_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours
  REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiry
} as const;

/**
 * When true, people created or invited from the panel are provisioned in the
 * identity provider and receive an activation email to set their own password,
 * so the create/invite forms must not ask for a password. This mirrors the
 * backend `AUTH_PROVISIONING_ENABLED` flag and is set per environment; keep the
 * two in sync.
 */
export const AUTH_PROVISIONING_ENABLED =
  import.meta.env.VITE_AUTH_PROVISIONING === 'true';

/**
 * Backend numeric role ids (mirror of ROLE_NAMES_BY_ID keys).
 */
export const ROLE_IDS = {
  ADMIN: 1,
  ORGANIZATION: 2,
  STAFF: 3,
  CLIENT: 4,
  ORG_MANAGER: 5,
} as const;

/**
 * Plan module ids (backend `modules` table, seeded 1-based).
 * A plan carries a `modules` array of these ids; a feature is unlocked for a
 * non-admin either by role or by the org's plan including the module.
 * 1 = schedules/departments, 2 = staff, 3 = caller fields (admin config),
 * 4 = QR codes (and connections), 5 = organization / org-manager management.
 */
export const MODULE_IDS = {
  SCHEDULES: 1,
  STAFF: 2,
  CALLER_FIELDS: 3,
  QRCODES: 4,
  ORGANIZATION: 5,
} as const;

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 1000,
} as const;

/**
 * User Roles
 *
 * Define the roles available in your application.
 * These should match your backend role definitions.
 */
export const USER_ROLES = {
  ADMIN: 'admin',
  ORGANIZATION: 'organization',
  STAFF: 'staff',
  CLIENT: 'client',
  ORG_MANAGER: 'organization-manager',
} as const;

/**
 * Backend role_id values mapped to role names
 */
export const ROLE_NAMES_BY_ID: Record<number, string> = {
  1: USER_ROLES.ADMIN,
  2: USER_ROLES.ORGANIZATION,
  3: USER_ROLES.STAFF,
  4: USER_ROLES.CLIENT,
  5: USER_ROLES.ORG_MANAGER,
};

/**
 * Status Colors for UI
 *
 * Consistent color coding for different statuses.
 */
export const STATUS_COLORS = {
  active: '#4caf50',
  inactive: '#9e9e9e',
  pending: '#ff9800',
  error: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
} as const;

/**
 * Client identifier the API expects in every request
 */
export const API_REQUEST_SOURCE = 'web';

/**
 * App version reported to the API (informational)
 */
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'admin';

/**
 * Numeric language ids of the API (1 = en, 2 = fr, 3 = es)
 */
export const LANGUAGE_API_IDS: Record<string, number> = {
  en: 1,
  fr: 2,
  es: 3,
};

/**
 * API language id for the active UI locale
 */
export const getApiLanguageId = (): number => {
  const locale = localStorage.getItem('locale') || 'es';
  return LANGUAGE_API_IDS[locale] ?? LANGUAGE_API_IDS.es;
};

/**
 * Language Options
 */
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_DATE: 'MMM D, YYYY',
  DISPLAY_DATETIME: 'MMM D, YYYY h:mm A',
} as const;

// ============================================
// DERIVED TYPES
// ============================================

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];
export type LanguageCode = (typeof LANGUAGE_OPTIONS)[number]['value'];

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getUserRoleValues = (): string[] => Object.values(USER_ROLES);

export const isValidUserRole = (role: string): role is UserRole => {
  return getUserRoleValues().includes(role);
};
